/**
 * Custom Hooks Documentation:
 *
 * usePagination:
 * A hook for handling pagination state and logic.
 *
 * @param {number} initialLimit - Initial number of items per page (default: 10)
 * @param {function} onPageChange - Callback function when page/limit changes
 *
 * @returns {object} Pagination controls and state
 * - page: Current page number
 * - limit: Items per page
 * - setPage: Function to update page
 * - setLimit: Function to update limit
 * - handleLimitChange: Function to handle items per page change
 *
 * Usage Example:
 * const { page, limit, setPage, handleLimitChange } = usePagination(10, (newPage, newLimit) => {
 *   dispatch(fetchData({ page: newPage, limit: newLimit }));
 * });
 */

import { useState } from "react";

export const usePagination = (initialLimit = 25, onPageChange, initialPage = 1) => {
  const [page, setPage] = useState(initialPage < 1 ? 1 : initialPage);
  const [limit, setLimit] = useState(initialLimit < 25 ? 25 : initialLimit);

  const handleLimitChange = (value) => {
    const newLimit = Number(value);
    const currentStartIndex = (page - 1) * limit + 1;
    const newPage = Math.ceil(currentStartIndex / newLimit);
    setLimit(newLimit);
    setPage(newPage);

    if (onPageChange) {
      onPageChange(newPage, newLimit);
    }
  };

  return {
    page,
    limit,
    setPage,
    setLimit,
    handleLimitChange,
  };
};
